import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useParams } from 'react-router-dom'

import { fehlertext } from '../../api/client'
import { BackIcon, XIcon } from '../../components/Icons'
import { Netzstreifen } from '../../components/Netzstreifen'
import { useEscape } from '../../hooks/useEscape'
import { veilCloses } from '../../lib/veil'
import { usePolls, useTrip } from '../../hooks/queries'
import { datumText } from '../../lib/ortszeit'
import { useTexte } from '../../texte'
import { Abstimmungskarte } from '../thread/Abstimmungskarte'

/**
 * All polls of a trip on one page (1.92.0, #116) -- the category
 * "Abstimmungen" on the trip page.
 *
 * **The card is the chat's card** (`Abstimmungskarte`): voting, closing and
 * the details come with it, and what each person sees is decided by the
 * server's answer, not by a role here -- a reader gets percentages after
 * voting, the author counts and names. A second card for this page would be
 * a second copy of the same rule.
 *
 * Newest first, as the server gives them. Two ways around: "Springe zu"
 * lists the questions and scrolls to one; "Im Chat zeigen" under each poll
 * opens the chat at its post (`?m=`, `useVerlaufsprung`), for the context
 * the question was asked in.
 */
export function AbstimmungenPage() {
  const texte = useTexte()
  const t = texte.abstimmungen
  const { tripId: roh } = useParams()
  const tripId = Number(roh)
  const polls = usePolls(tripId)
  const verlauf = useTrip(tripId).data?.zones ?? null
  const [liste, setListe] = useState(false)
  const [markiert, setMarkiert] = useState<number | null>(null)
  const uhr = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => clearTimeout(uhr.current), [])

  const alle = polls.data?.polls ?? []

  function springe(id: number) {
    setListe(false)
    document.getElementById(`abst-${id}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    setMarkiert(id)
    clearTimeout(uhr.current)
    uhr.current = setTimeout(() => setMarkiert(null), 1600)
  }

  return (
    <div className="abstimmungen" data-abstimmungen="">
      <Netzstreifen />
      <header className="bar">
        <Link className="icon-button" to={`/t/${tripId}/info`} aria-label={t.zurueck}>
          <BackIcon />
        </Link>
        <h2 className="bar__title bar__grow">{t.titel}</h2>
        {alle.length > 1 && (
          <button type="button" className="button button--outline button--schmal" onClick={() => setListe(true)} data-abstimmungen-springe="">
            {t.springeZu}
          </button>
        )}
      </header>
      <div className="abstimmungen__inhalt">
        {polls.isError && <p className="notice" role="alert">{fehlertext(polls.error)}</p>}
        {polls.data !== undefined && alle.length === 0 && <p className="leise">{t.keine}</p>}
        {alle.map((poll) => (
          <section
            key={poll.id}
            id={`abst-${poll.id}`}
            className={markiert === poll.id ? 'abstimmungen__eintrag abstimmungen__eintrag--markiert' : 'abstimmungen__eintrag'}
            data-abstimmung-id={poll.id}
          >
            <p className="abstimmungen__datum">{datumText(poll.created_at, verlauf)}</p>
            <Abstimmungskarte poll={poll} tripId={tripId} />
            {poll.message_id !== null && (
              <Link className="abstimmungen__chat" to={`/t/${tripId}?m=${poll.message_id}`} data-abstimmung-chat={poll.message_id}>
                {t.imChat}
              </Link>
            )}
          </section>
        ))}
      </div>
      {liste && (
        <Sprungliste
          fragen={alle.map((p) => ({ id: p.id, frage: p.question, datum: datumText(p.created_at, verlauf) }))}
          onWahl={springe}
          onSchliessen={() => setListe(false)}
        />
      )}
    </div>
  )
}

/** The questions as a sheet, one tap each -- built like `Tagestrennerliste`. */
function Sprungliste({
  fragen,
  onWahl,
  onSchliessen,
}: {
  fragen: Array<{ id: number; frage: string; datum: string }>
  onWahl: (id: number) => void
  onSchliessen: () => void
}) {
  const texte = useTexte()
  const t = texte.abstimmungen
  useEscape(onSchliessen)

  return createPortal(
    <div className="bogen bogen--obenauf bogen--schliesst" role="dialog" aria-modal="true" aria-label={t.listeTitel} data-abstimmungen-liste="" onClick={veilCloses(onSchliessen)}>
      <div className="bogen__blatt bogen__blatt--gegliedert">
        <div className="bogen__kopf">
          <h3 className="bogen__titel">{t.listeTitel}</h3>
          <button type="button" className="icon-button" onClick={onSchliessen} aria-label={texte.allgemein.schliessen} title={texte.allgemein.schliessen}>
            <XIcon size={18} />
          </button>
        </div>
        <div className="bogen__rolle">
          <ul className="tageswahl__liste">
            {fragen.map((f) => (
              <li key={f.id}>
                <button type="button" className="tageswahl__zeile" onClick={() => onWahl(f.id)} data-abstimmung-wahl={f.id}>
                  <span>{f.frage}</span>
                  <span className="tageswahl__anzahl">{f.datum}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>,
    document.body,
  )
}
